import type { RealtimeSubscriptionAdapter } from '../types/subscription-adapter.types';
import { useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ErrorReporter from 'src/monitoring/errorReporter';
import type { RealtimeContextValue } from '../types/realtime-context.type';
import type { RealtimeEventHandler } from '../types/realtime.types';
import {
  isRealtimeAckMessage,
  isRealtimeEventMessage,
} from '../utils/socket-message.util';
import { useRealtimeSubscriptions } from './use-realtime-subscriptions';
import { isSocketOpen } from '../utils/socket-connection.util';
import { useRealtimeConnection } from './use-realtime-connection';

/**
 * Coordinates topic policy and the transport for one authenticated provider.
 * @param adapters - Stable application-owned subscription policies.
 * @returns Public realtime state and listener/subscription registration.
 */
export const useRealtimeClient = (
  adapters: readonly RealtimeSubscriptionAdapter[]
): RealtimeContextValue => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const socketRef = useRef<WebSocket | null>(null);
  const eventHandlersRef = useRef<Set<RealtimeEventHandler>>(new Set());
  const {
    topicStatuses,
    topicErrors,
    subscribe,
    handleAckMessage,
    canDeliverEvent,
    resubscribeCurrentTopics,
    resetAuthorization,
  } = useRealtimeSubscriptions({ socketRef, getAccessTokenSilently, adapters });
  const addEventListener = useCallback((handler: RealtimeEventHandler) => {
    eventHandlersRef.current.add(handler);

    return () => {
      eventHandlersRef.current.delete(handler);
    };
  }, []);

  const handleSocketMessage = useCallback(
    (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'string') {
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        ErrorReporter.sendException(error);
        return;
      }

      if (isRealtimeAckMessage(parsed)) {
        handleAckMessage(parsed);
        return;
      }

      if (!isRealtimeEventMessage(parsed)) {
        return;
      }

      if (!canDeliverEvent(parsed.topic)) return;
      eventHandlersRef.current.forEach((handler) => handler(parsed));
    },
    [canDeliverEvent, handleAckMessage]
  );
  const status = useRealtimeConnection({
    socketRef,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    handleSocketMessage,
    resubscribeCurrentTopics,
    resetAuthorization,
  });
  // Capture this generation so an old domain cleanup cannot send on a replacement socket.
  const transport = socketRef.current;
  const sendControl = useCallback<RealtimeContextValue['sendControl']>(
    (message) => {
      if (
        !isAuthenticated ||
        isLoading ||
        status !== 'connected' ||
        !transport ||
        socketRef.current !== transport ||
        !isSocketOpen(transport)
      ) {
        return false;
      }
      try {
        transport.send(JSON.stringify(message));
        return true;
      } catch {
        // Control observations are best effort; domain expiry provides recovery.
        return false;
      }
    },
    [isAuthenticated, isLoading, status, transport]
  );
  return useMemo(
    () => ({
      status,
      topicStatuses,
      topicErrors,
      subscribe,
      addEventListener,
      sendControl,
    }),
    [
      addEventListener,
      status,
      subscribe,
      topicErrors,
      topicStatuses,
      sendControl,
    ]
  );
};
